import { Active } from '@dnd-kit/core';
import { cloneDeep, isNil } from 'lodash';
import { IBuilder, IRowMeta } from '../../redux/slices/oldBuilder';
import { getMeasureFunction } from '../../redux/slices/oldBuilder/nextSteps/utils';
import {
  FilterType,
  IBasicFilterFieldsFragment,
  IFieldFieldsFragment,
  ILogicalOperator,
  IMeasureFunction,
  IRelationalOperator,
} from '../../utils/apollo/graphqlSchema';
import { IEventEntitySelection } from '../Builder/BuilderFields/BuilderEventsInput/types';
import { defaultPredicate } from '../FilterWidget/types';
import {
  prepareCondition,
  preparePredicate,
} from '../FilterWidget/utils/getFilterWidgetOutput';
import { EntityDropzoneType } from '../QuickAccessDictionary/DictionaryTabs/types';

export const createNewBasicFilter = ({
  entity,
  relOp,
}: {
  entity: IFieldFieldsFragment;
  relOp: IRelationalOperator;
}): IBasicFilterFieldsFragment => {
  const newCondition = prepareCondition({
    predicates: [
      {
        ...defaultPredicate,
        field: entity,
        relOp,
      },
    ],
    logicOps: [],
  });
  const conditions = [newCondition];
  return {
    __typename: 'BasicFilter',
    conditions,
    logicOps: [],
    type: FilterType.Basic,
  };
};

export const updateFilterWithCondition = ({
  filter,
  conditionIndex,
  predicateIndex,
  entity,
  relOp,
  logicOp,
}: {
  filter: IBasicFilterFieldsFragment;
  conditionIndex: number;
  predicateIndex?: number;
  entity: IFieldFieldsFragment;
  relOp: IRelationalOperator;
  logicOp: ILogicalOperator;
}) => {
  const newFilter = cloneDeep(filter);
  const { conditions, logicOps } = newFilter;

  if (!isNil(predicateIndex)) {
    const condition = conditions[conditionIndex];
    const predicates = condition.predicates;
    predicates.splice(
      predicateIndex,
      0,
      preparePredicate({
        ...defaultPredicate,
        field: entity,
        relOp,
      }),
    );
    condition.logicOps.push(logicOp);
  } else {
    const newCondition = prepareCondition({
      predicates: [
        {
          ...defaultPredicate,
          field: entity,
          relOp,
        },
      ],
      logicOps: [],
    });
    conditions.splice(conditionIndex, 0, newCondition);
    logicOps.push(logicOp);
  }

  return newFilter;
};

export const createDictionaryEntityDropzoneId = (
  entityType: EntityDropzoneType,
  rowMeta: IRowMeta,
  suffix: string = '',
): string => {
  const appendProperty = (property: any, propertyName: string): string => {
    return !isNil(property) ? `-${propertyName}-${property}` : '';
  };

  return (
    `${entityType}-droppable` +
    appendProperty(rowMeta.rowType, 'rowType') +
    appendProperty(rowMeta.rowIndex, 'rowIndex') +
    appendProperty(rowMeta.eventIndex, 'eventIndex') +
    appendProperty(rowMeta.conditionIndex, 'conditionIndex') +
    appendProperty(rowMeta.predicateIndex, 'predicateIndex') +
    appendProperty(rowMeta.numberIndex, 'numberIndex') +
    appendProperty(rowMeta.negativeStepRowIndex, 'negativeStepRowIndex') +
    appendProperty(rowMeta.pathDirection, 'pathDirection') +
    appendProperty(rowMeta.isLastStep, 'isLastStep') +
    appendProperty(rowMeta.cohortRowType, 'cohortRowType') +
    suffix
  );
};

export const checkExistingEvent = (
  existingEvents: Array<IEventEntitySelection> | undefined,
  entity: IEventEntitySelection,
): boolean => {
  return !!existingEvents?.find((item) => {
    if (
      item.__typename === 'VirtualEvent' &&
      entity.__typename === 'VirtualEvent'
    ) {
      return item.id === entity.id;
    } else if (
      item.__typename === 'BasicEventValue' &&
      entity.__typename === 'BasicEventValue'
    ) {
      return item.value.key === entity.value.key;
    }
    return false;
  });
};

// naive check on whether the existingEvents and the event entity
// are of the same type. reason is we don't want to mix virtual & basic events
export const checkExistingEventsEntityType = (
  existingEvents: Array<IEventEntitySelection> | undefined,
  entity: IEventEntitySelection,
): boolean => {
  if (existingEvents?.length) {
    const firstEvent = existingEvents[0];
    return firstEvent.__typename === entity.__typename;
  } else {
    return true;
  }
};

// gets the data from dnd-kit/core's Active type
export const getDndActiveItemData = (activeItem: Active | null) =>
  activeItem?.data?.current?.data;

// KB-7567: we are hardcoding this measure function bc
// defaultMeasureFunctions in builder are only filled for Query & DataTable reports
// all other reports is empty, such as Cohorts, Prediction, etc. fallback to Count Events
const uniqueUsersFunction: IMeasureFunction = {
  key: 'COUNT_EVENTS',
  name: 'Count Events',
  fieldRequired: false,
  requirePercentile: false,
  forceBreakdownOverall: false,
  __typename: 'MeasureFunction',
};

export const getDndMeasureFunction = (
  defaultMeasureFunctions: IBuilder['defaultMeasureFunctions'],
  subjectName: string | null,
) => {
  if (defaultMeasureFunctions) {
    const fcn = getMeasureFunction(defaultMeasureFunctions, subjectName);

    if (fcn) {
      return fcn;
    }
  }
  return uniqueUsersFunction;
};
